import type {
  CdActionStep,
  CdComparisonBar,
  CdContentAdvantage,
  CdContentGap,
  CdCrawledPage,
  CdCrawlMeta,
  CdCrawlSubject,
  CdCrawlReportData,
  CdKeywordCompare,
  CdScoreCard,
  CdSeverity,
  CdDossierHeader,
  CdStrength,
  CdTechnicalCheck,
  CdTechnicalStatus,
  CdWeakness,
  CmpCompetitor,
  CmpPracticeType,
  CmpSource,
  CmpStat,
} from '@elva/ui';
import { growthGet, growthGetBlob, growthList, growthPost, growthPostAsync } from '@/data/http';
import { downloadBlob } from '@/data/download';
import { getErrorStatus } from '@/data/errors';
import type { MarketingMeta } from '@/data/marketingApiTypes';
import { deepCamel, num, str } from '@/data/adapt';

interface BeCompetitorPipeline {
  competitor_id?: number;
  location_id?: number;
  business_name?: string;
  website?: string;
  distance_miles?: number;
  rating?: number;
  review_count?: number;
  reviews_per_month?: string;
  active_ads?: number | unknown;
  top_keywords?: string[] | unknown;
  threat_score?: number;
  practice_type?: string | null;
  moat?: string[] | string | null;
  selection_source?: string;
  is_active?: boolean;
}

interface BeCompetitor {
  id: string;
  pipeline_competitor_id?: number;
  is_active?: boolean;
  discovered_by?: string | null;
  from_pipeline?: BeCompetitorPipeline;
  name?: string;
  domain?: string;
  address?: string;
  distance_miles?: number;
  threat_score?: number;
  rating?: number;
  reviews?: number;
  review_velocity?: string;
  active_ads?: number | unknown;
  top_keywords?: string[] | unknown;
  type?: string;
  practice_type?: string;
  moat?: string | string[] | null;
  source?: string;
}

interface BeDossier {
  competitor?: Record<string, unknown>;
  header?: Record<string, unknown>;
  profile?: unknown;
  review_sentiment?: unknown;
  ads?: unknown;
  swots?: unknown[];
  swot?: unknown[];
  data_freshness?: Record<string, string | null>;
}

interface BeCrawlReport {
  data_status?: 'pipeline_ready' | 'pipeline_pending';
  subject?: Record<string, unknown>;
  subjects?: Array<Record<string, unknown>>;
  competitor?: Record<string, unknown>;
  scores?: Array<Record<string, unknown>>;
  their_strengths?: Array<Record<string, unknown>>;
  their_weaknesses?: Array<Record<string, unknown>>;
  comparison_bars?: Array<Record<string, unknown>>;
  action_steps?: Array<Record<string, unknown>>;
  strategic_insight?: string | null;
  content_gaps?: Array<Record<string, unknown>>;
  content_advantages?: Array<Record<string, unknown>>;
  keyword_compare?: Array<Record<string, unknown>>;
  technical_checks?: Array<Record<string, unknown>>;
  crawled_pages?: Array<Record<string, unknown>>;
  page_count?: number | null;
  crawl_meta?: Record<string, unknown>;
}

export const COMPETITORS_PAGE_SIZE = 25;

export interface CompetitorsPagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface CompetitorsListData {
  items: CmpCompetitor[];
  stats: CmpStat[];
  pagination: CompetitorsPagination;
}

const PRACTICE_TYPES: CmpPracticeType[] = [
  'Corporate DSO',
  'Group Practice',
  'Private',
  'Solo Practice',
];

function domainFromWebsite(website: string | undefined): string {
  if (!website?.trim()) return '—';
  try {
    const url = website.startsWith('http') ? website : `https://${website}`;
    return new URL(url).hostname.replace(/^www\./i, '');
  } catch {
    return website.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0] ?? '—';
  }
}

function adaptMoat(moat: string | string[] | null | undefined): string | null {
  if (moat == null) return null;
  if (Array.isArray(moat)) return moat.length ? moat.join(', ') : null;
  return moat.trim() ? moat : null;
}

function adaptActiveAds(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (Array.isArray(value)) return value.length;
  return 0;
}

function adaptTopKeywords(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item)).filter(Boolean);
}

function adaptSource(row: BeCompetitor, pipeline?: BeCompetitorPipeline): CmpSource {
  const src = (row.source ?? pipeline?.selection_source ?? row.discovered_by ?? '').toLowerCase();
  if (src.includes('manual')) return 'manual';
  return 'auto';
}

function shortenKeyword(keyword: string): string {
  const core = (keyword.split(' - ')[0] ?? keyword).trim();
  if (core.length <= 22) return core;
  return `${core.slice(0, 20)}…`;
}

function adaptCompetitor(row: BeCompetitor): CmpCompetitor | null {
  if (!row.id) return null;
  const pipeline = row.from_pipeline;
  const typeRaw = str(row.type ?? row.practice_type ?? pipeline?.practice_type, 'Private');
  const type = PRACTICE_TYPES.includes(typeRaw as CmpPracticeType)
    ? (typeRaw as CmpPracticeType)
    : 'Private';

  const rawKeywords = adaptTopKeywords(row.top_keywords ?? pipeline?.top_keywords);

  return {
    id: String(row.id),
    name: str(row.name ?? pipeline?.business_name),
    domain: str(row.domain, domainFromWebsite(pipeline?.website)),
    address: str(row.address, '—'),
    distanceMiles: num(row.distance_miles ?? pipeline?.distance_miles),
    threatScore: num(row.threat_score ?? pipeline?.threat_score),
    rating: num(row.rating ?? pipeline?.rating),
    reviews: num(row.reviews ?? pipeline?.review_count),
    reviewVelocity: str(row.review_velocity ?? pipeline?.reviews_per_month, '—'),
    activeAds: adaptActiveAds(row.active_ads ?? pipeline?.active_ads),
    topKeywords: rawKeywords.map(shortenKeyword),
    type,
    moat: adaptMoat(row.moat ?? pipeline?.moat),
    source: adaptSource(row, pipeline),
  };
}

function adaptStats(items: CmpCompetitor[]): CmpStat[] {
  const avgThreat =
    items.length > 0
      ? Math.round(items.reduce((a, c) => a + c.threatScore, 0) / items.length)
      : 0;
  const withAds = items.filter((c) => c.activeAds > 0).length;

  return [
    {
      key: 'total',
      label: 'Competitors Tracked',
      value: String(items.length),
      delta: 'In your market radius',
      tone: 'neutral',
    },
    {
      key: 'threat',
      label: 'Avg Threat Score',
      value: String(avgThreat),
      delta: 'Higher = more competitive pressure',
      tone: avgThreat >= 70 ? 'terra' : 'gold',
    },
    {
      key: 'ads',
      label: 'Running Google Ads',
      value: String(withAds),
      delta: `${items.length - withAds} not advertising`,
      tone: 'info',
    },
    {
      key: 'reviews',
      label: 'Top Review Count',
      value: String(Math.max(...items.map((c) => c.reviews), 0)),
      delta: 'Among tracked competitors',
      tone: 'success',
    },
  ];
}

function adaptSummaryStats(summary: { tiles?: Array<Record<string, unknown>> } | undefined): CmpStat[] | undefined {
  if (!summary?.tiles?.length) return undefined;
  return summary.tiles.map((tile) => {
    const c = deepCamel<Record<string, unknown>>(tile);
    return {
      key: str(c.key ?? c.label, 'stat'),
      label: str(c.label),
      value: str(c.value),
      delta: str(c.sub ?? c.delta, '—'),
      tone: 'neutral',
    };
  });
}

function adaptPagination(
  meta: MarketingMeta | undefined,
  page: number,
  pageSize: number,
  itemCount: number,
): CompetitorsPagination {
  const totalCount = num(meta?.total_count, itemCount);
  const totalPages = Math.max(1, num(meta?.total_pages, Math.ceil(totalCount / pageSize)));
  return {
    page: num(meta?.page, page),
    pageSize: num(meta?.page_size, pageSize),
    totalCount,
    totalPages,
  };
}

function adaptDossierHeader(be: BeDossier): CdDossierHeader {
  const competitor = be.competitor ? deepCamel<Record<string, unknown>>(be.competitor) : {};
  const h = deepCamel<Record<string, unknown>>(be.header ?? competitor ?? be);
  const name = str(h.name ?? competitor.name);
  const moatRaw = h.moat ?? competitor.moat;

  return {
    initials: str(
      h.initials ?? (name ? name.slice(0, 2).toUpperCase() : '??'),
      '??',
    ),
    name,
    address: str(h.address ?? competitor.address),
    distance:
      h.distance != null
        ? String(h.distance)
        : competitor.distanceMiles != null
          ? `${competitor.distanceMiles} mi`
          : '—',
    rating: num(h.rating ?? competitor.rating),
    reviews: num(h.reviews ?? competitor.reviews),
    threatScore: num(h.threatScore ?? h.threat_score ?? competitor.threatScore ?? competitor.threat_score),
    moat: adaptMoat(moatRaw as string | string[] | null) ?? '—',
    segments: (h.segments as string[] | undefined) ?? [],
  };
}

function adaptCrawlSubject(row: Record<string, unknown>): CdCrawlSubject {
  const c = deepCamel<Record<string, unknown>>(row);
  return {
    id: str(c.id, 'subject'),
    name: str(c.name),
    distance: str(c.distance, '—'),
    rating: num(c.rating),
    reviews: num(c.reviews),
    url: str(c.url ?? c.domain, '—'),
  };
}

function adaptSeverity(value: unknown): CdSeverity {
  const key = String(value ?? 'medium').toLowerCase();
  if (key === 'high' || key === 'low') return key;
  return 'medium';
}

function adaptTechnicalStatus(value: unknown): CdTechnicalStatus {
  const key = String(value ?? 'warning').toLowerCase();
  if (key === 'good' || key === 'bad') return key;
  return 'warning';
}

function adaptCrawlMeta(raw: Record<string, unknown> | undefined | null): CdCrawlMeta | null {
  if (!raw) return null;
  const c = deepCamel<Record<string, unknown>>(raw);
  const last = str(c.lastCrawled ?? c.last_crawled, '');
  const next = str(c.nextCrawl ?? c.next_crawl, '');
  if (!last && !next) return null;
  return { lastCrawled: last || '—', nextCrawl: next || '—' };
}

function adaptCrawlReport(be: BeCrawlReport): CdCrawlReportData {
  const subjectRow = be.subject ?? be.competitor ?? be.subjects?.[0];
  const subject = subjectRow ? adaptCrawlSubject(subjectRow) : undefined;

  const scores: CdScoreCard[] = (be.scores ?? []).map((row, index) => {
    const c = deepCamel<Record<string, unknown>>(row);
    const scoreRaw = c.score;
    return {
      id: str(c.id, `score-${index}`),
      label: str(c.label),
      score: scoreRaw == null ? null : num(scoreRaw),
      desc: str(c.desc ?? c.description, '—'),
    };
  });

  return {
    dataStatus: be.data_status ?? (scores.some((s) => s.score == null) ? 'pipeline_pending' : 'pipeline_ready'),
    ...(subject ? { subject } : {}),
    scores,
    strengths: (be.their_strengths ?? []).map((row, index) => {
      const c = deepCamel<Record<string, unknown>>(row);
      return { id: str(c.id, `strength-${index}`), text: str(c.text) };
    }),
    weaknesses: (be.their_weaknesses ?? []).map((row, index) => {
      const c = deepCamel<Record<string, unknown>>(row);
      return {
        id: str(c.id, `weakness-${index}`),
        text: str(c.text),
        severity: adaptSeverity(c.severity),
      };
    }),
    comparisonBars: (be.comparison_bars ?? []).map((row, index) => {
      const c = deepCamel<Record<string, unknown>>(row);
      return {
        id: str(c.id, `bar-${index}`),
        label: str(c.label),
        yours: num(c.yours),
        theirs: num(c.theirs),
        ...(c.maxVal != null ? { maxVal: num(c.maxVal) } : {}),
      };
    }),
    actionSteps: (be.action_steps ?? []).map((row, index) => {
      const c = deepCamel<Record<string, unknown>>(row);
      return {
        num: str(c.num ?? index + 1),
        title: str(c.title),
        desc: str(c.desc ?? c.description),
      };
    }),
    strategicInsight: be.strategic_insight ?? null,
    contentGaps: (be.content_gaps ?? []).map((row, index) => {
      const c = deepCamel<Record<string, unknown>>(row);
      const priority = str(c.priority, 'Medium');
      return {
        id: str(c.id, `gap-${index}`),
        topic: str(c.topic),
        traffic: str(c.traffic, '—'),
        difficulty: str(c.difficulty, '—'),
        priority: (priority === 'High' || priority === 'Low' ? priority : 'Medium') as CdContentGap['priority'],
      };
    }),
    contentAdvantages: (be.content_advantages ?? []).map((row, index) => {
      const c = deepCamel<Record<string, unknown>>(row);
      return {
        id: str(c.id, `adv-${index}`),
        topic: str(c.topic),
        yourRank: str(c.yourRank ?? c.your_rank, '—'),
        theirRank: str(c.theirRank ?? c.their_rank, '—'),
        traffic: str(c.traffic, '—'),
      };
    }),
    keywordCompare: (be.keyword_compare ?? []).map((row, index) => {
      const c = deepCamel<Record<string, unknown>>(row);
      return {
        id: str(c.id, `kw-${index}`),
        keyword: str(c.keyword),
        volume: str(c.volume, '—'),
        you: str(c.you, '—'),
        them: str(c.them, '—'),
        gap: num(c.gap),
      };
    }),
    technicalChecks: (be.technical_checks ?? []).map((row, index) => {
      const c = deepCamel<Record<string, unknown>>(row);
      return {
        id: str(c.id, `tech-${index}`),
        label: str(c.label),
        value: str(c.value, '—'),
        status: adaptTechnicalStatus(c.status),
        benchmark: str(c.benchmark, '—'),
      };
    }),
    crawledPages: (be.crawled_pages ?? []).map((row, index) => {
      const c = deepCamel<Record<string, unknown>>(row);
      const pageType = str(c.type, 'Service');
      return {
        id: str(c.id, `page-${index}`),
        url: str(c.url),
        title: str(c.title),
        type: (pageType === 'Blog' || pageType === 'Landing' ? pageType : 'Service') as CdCrawledPage['type'],
        seoScore: num(c.seoScore ?? c.seo_score),
        issues: num(c.issues),
        traffic: str(c.traffic, '—'),
      };
    }),
    pageCount: be.page_count ?? null,
    crawlMeta: adaptCrawlMeta(be.crawl_meta),
  };
}

export async function fetchCompetitors(options?: {
  page?: number;
  limit?: number;
}): Promise<CompetitorsListData> {
  const page = options?.page ?? 1;
  const limit = options?.limit ?? COMPETITORS_PAGE_SIZE;

  const [{ items: rows, meta }, summary] = await Promise.all([
    growthList<BeCompetitor>('/competitors/', { page, limit }),
    growthGet<{ tiles?: Array<Record<string, unknown>> }>('/competitors/summary/').catch(() => undefined),
  ]);

  const items = rows.map(adaptCompetitor).filter((row): row is CmpCompetitor => row != null);
  const stats = adaptSummaryStats(summary) ?? adaptStats(items);
  const pagination = adaptPagination(meta, page, limit, items.length);

  return { items, stats, pagination };
}

export async function fetchCompetitorDossier(id: string): Promise<CdDossierHeader | null> {
  try {
    const be = await growthGet<BeDossier>(`/competitors/${encodeURIComponent(id)}/dossier/`);
    return adaptDossierHeader(be);
  } catch (error) {
    if (getErrorStatus(error) === 404) return null;
    throw error;
  }
}

export async function fetchCompetitorCrawl(id: string): Promise<CdCrawlReportData | null> {
  try {
    const be = await growthGet<BeCrawlReport>(`/competitors/${encodeURIComponent(id)}/crawl/`);
    return adaptCrawlReport(be);
  } catch (error) {
    if (getErrorStatus(error) === 404) return null;
    throw error;
  }
}

export async function addCompetitor(body: {
  name?: string;
  domain?: string;
  website_url?: string;
  address?: string;
}) {
  return growthPost('/competitors/', {
    name: body.name ?? body.domain ?? '',
    website_url: body.website_url ?? body.domain,
    ...(body.address ? { address: body.address } : {}),
  });
}

export async function removeCompetitor(id: string) {
  return growthPost(`/competitors/${encodeURIComponent(id)}/remove/`);
}

export async function rebuildCompetitors() {
  return growthPostAsync('/competitors/rebuild/');
}

export async function recrawlCompetitor(id: string) {
  return growthPostAsync(`/competitors/${encodeURIComponent(id)}/recrawl/`);
}

export async function exportCompetitorCrawlPdf(id: string): Promise<void> {
  const blob = await growthGetBlob(`/competitors/${encodeURIComponent(id)}/crawl/export/`, {
    format: 'pdf',
  });
  downloadBlob(blob, `competitor-${id}-crawl-report.pdf`);
}
